import type { NextSeoProps } from "next-seo";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { DefaultTitleTemplate } from "shared/components/meta/consts";
import {
  useDeepLinkTags,
  useDynamicMetaTags,
  useKeywordMetaTags,
} from "shared/components/meta/hooks";
import { useCanonicalUrl } from "shared/hooks";
import { getRssFeedLink } from "shared/lib/utils";

import { PageSeo } from "scmp-app/components/meta";
import type { topicSeoTopic$key } from "scmp-app/queries/__generated__/topicSeoTopic.graphql";

type Props = {
  extraSeoOptions?: NextSeoProps;
  reference: topicSeoTopic$key;
};

export const TopicSeo: FunctionComponent<Props> = ({ extraSeoOptions = {}, reference }) => {
  const topic = useFragment(
    graphql`
      fragment topicSeoTopic on Topic {
        name
        description {
          text
        }
        entityId
        urlAlias
        metaDescription
        image(type: OG) {
          url
        }
        ...hooksUseDeepLinkTagsBase
        ...hooksUseDynamicMetaTags
      }
    `,
    reference,
  );

  const rssFeedLink = getRssFeedLink(topic?.entityId);

  const metaTags = useDynamicMetaTags(topic);
  const keywordTags = useKeywordMetaTags(metaTags.controlled.keywords ?? topic.name);
  const deepLinkTags = useDeepLinkTags(topic);

  let titleTemplate = DefaultTitleTemplate;
  const title = useMemo(
    () => metaTags.controlled.title ?? topic.name,
    [metaTags.controlled.title, topic.name],
  );
  if (metaTags.controlled.title) {
    titleTemplate = "%s";
  }

  const description = useMemo(
    () =>
      findFirstNonEmptyString(
        metaTags.controlled.description,
        topic.metaDescription,
        topic.description?.text,
        `The latest news, analysis and opinion on ${topic.name}. In-depth analysis, industry insights and expert opinion.`,
      ),
    [metaTags.controlled.description, topic.description?.text, topic.metaDescription, topic.name],
  );

  const { canonicalUrl } = useCanonicalUrl(topic.urlAlias);

  return (
    <PageSeo
      {...extraSeoOptions}
      additionalLinkTags={[rssFeedLink]}
      additionalMetaTags={[...keywordTags, ...deepLinkTags]}
      canonical={canonicalUrl}
      description={description}
      openGraph={{
        description,
        images: topic.image?.url ? [{ url: topic.image?.url }] : [],
        title,
        url: canonicalUrl,
      }}
      title={title}
      titleTemplate={titleTemplate}
    />
  );
};

TopicSeo.displayName = "TopicSeo";
